/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, useContext, useState } from 'react';
import { cleanPhoneNumber, removeLeadingZeros, formatFullPhoneNumber } from '@leedpulse-monorepo/utils';
import { UserRole, UserType, ContextStatus, ECreditType } from '@leedpulse-monorepo/enums';
import createUser, {
  AIPayload,
  CreateUserFailure,
  CreateUserPayload,
  SubscriptionPayload,
  UserInfosPayload,
} from '../../../../domain/use_cases/users/createUser';
import AuthenticationContext from '../../auth/AuthenticationContext';
import AIGender from '../../../../domain/enums/ai/AIGender';
import SubscriptionTimezoneCity from '../../../../domain/enums/subscription/subscription_timezone/SubscriptionTimezoneCity';
import SubscriptionTimezoneContinent from '../../../../domain/enums/subscription/subscription_timezone/SubscriptionTimezoneContinent';
import SubscriptionTimezoneCountry from '../../../../domain/enums/subscription/subscription_timezone/SubscriptionTimezoneCountry';
import AITon from '../../../../domain/enums/ai/AITon';
import SubscriptionSettings from '../../../../domain/models/subscription/SubscriptionSettings';
import SubscriptionDataSource from '../../../../domain/enums/subscription/subscription_datasource/SubscriptionDataSource';
import { formatErrorMessage } from '../../../../core/utils/errorUtils';

export interface UserCreationFormContextProps {
  status: ContextStatus;
  user: UserInfosPayload;
  linkedSubscriptionId: string;
  linkedCompanyId: string;
  subscription: SubscriptionPayload;
  ai: AIPayload;
  errorMessage: string;
  selectedCountry: { name: string; code: string; digitsLength: number };
  phoneNumber: string;
  onAvatarChange: (avatar: string) => void;
  onUserEmailChange: (email: string) => void;
  onUserEmailAuthenticationChange: (emailAuthentication: string) => void;
  onUserPasswordChange: (password: string) => void;
  onUserRoleChange: (role: UserRole) => void;
  onUserNotesChange: (notes: string) => void;
  onUserFirstnameChange: (firstname: string) => void;
  onUserLastnameChange: (lastname: string) => void;
  onUserPhoneChange: (phone: string) => void;
  onUserSelectedCountryCodeChange: (selectedCountry: { name: string; code: string; digitsLength: number }) => void;
  onUserPhoneNumberChange: (newPhoneNumber: string) => void;
  onUserTypeChange: (type: UserType) => void;
  onLinkedSubscriptionIdChange: (id: string) => void;
  onLinkedCompanyIdChange: (id: string) => void;
  onSubscriptionIdPlanChange: (idPlan: string) => void;
  onSubscriptionTimezoneCityChange: (city: SubscriptionTimezoneCity) => void;
  onSubscriptionTimezoneContinentChange: (continent: SubscriptionTimezoneContinent) => void;
  onSubscriptionTimezoneCountryChange: (country: SubscriptionTimezoneCountry) => void;
  onSubscriptionNameChange: (name: string) => void;
  onSubscriptionSettingsEmailNotificationCopyChange: (copies: string[]) => void;
  onSubscriptionSettingsBetaAccountChange: (beta_account: boolean) => void;
  onSubscriptionSettingsDemoAccountChange: (demo_account: boolean) => void;
  onSubscriptionSettingsPaygEnabledChange: (payg_enabled: boolean) => void;
  onSubscriptionSettingsPaymentWallChange: (payment_wall: boolean) => void;
  onSubscriptionNotesChange: (notes: string) => void;
  onSubscriptionSenderChange: (sender: string) => void;
  onSubscriptionCreditsChange: (type: ECreditType, value: number) => void;
  onSubscriptionPlanStartChange: (planStart: Date) => void;
  onSubscriptionPlanEndChange: (planEnd: Date) => void;
  onSubscriptionDataSourceChange: (datasource: SubscriptionDataSource) => void;
  onAIActionChange: (action: string) => void;
  onAIBehaviourChange: (behaviour: string) => void;
  onAIWebsiteChange: (website: string) => void;
  onAIConstraintsChange: (constraints: string) => void;
  onAIDomainChange: (domain: string) => void;
  onAIFullnameChange: (fullname: string) => void;
  onAIGenderChange: (gender: AIGender) => void;
  onAIIntroChange: (intro: string) => void;
  onAIFirstReminderTimingChange: (firstReminder: number) => void;
  onAISecondReminderTimingChange: (secondReminder: number) => void;
  onAILocationChange: (location: string) => void;
  onAIProfileChange: (profile: string) => void;
  onAITargetChange: (target: string) => void;
  onAITonChange: (ton: AITon) => void;
  onEmployeeInChargeChange: (employeeInChargeUid: string) => void;
  submit: () => Promise<void>;
}

const UserCreationFormContext = createContext<UserCreationFormContextProps>({
  status: ContextStatus.IDLE,
  user: UserInfosPayload.newDefault(),
  linkedSubscriptionId: '',
  linkedCompanyId: '',
  subscription: SubscriptionPayload.newDefault(''),
  ai: AIPayload.newDefault(),
  errorMessage: '',
  selectedCountry: { name: 'france', code: '33', digitsLength: 9 },
  phoneNumber: '',
  onAvatarChange: (_: string) => { },
  onUserEmailChange: (_: string) => { },
  onUserEmailAuthenticationChange: (_: string) => { },
  onUserPasswordChange: (_: string) => { },
  onUserRoleChange: (_: UserRole) => { },
  onUserNotesChange: (_: string) => { },
  onUserFirstnameChange: (_: string) => { },
  onUserLastnameChange: (_: string) => { },
  onUserPhoneChange: (_: string) => { },
  onUserSelectedCountryCodeChange: (_: { name: string; code: string; digitsLength: number }) => { },
  onUserPhoneNumberChange: (_: string) => { },
  onUserTypeChange: (_: UserType) => { },
  onLinkedSubscriptionIdChange: (_: string) => { },
  onLinkedCompanyIdChange: (_: string) => { },
  onSubscriptionIdPlanChange: (_: string) => { },
  onSubscriptionTimezoneCityChange: (_: SubscriptionTimezoneCity) => { },
  onSubscriptionTimezoneContinentChange: (
    _: SubscriptionTimezoneContinent
  ) => { },
  onSubscriptionTimezoneCountryChange: (_: SubscriptionTimezoneCountry) => { },
  onSubscriptionNameChange: (_: string) => { },
  onSubscriptionSettingsEmailNotificationCopyChange: (_: string[]) => { },
  onSubscriptionSettingsBetaAccountChange: (_: boolean) => { },
  onSubscriptionSettingsDemoAccountChange: (_: boolean) => { },
  onSubscriptionSettingsPaygEnabledChange: (_: boolean) => { },
  onSubscriptionSettingsPaymentWallChange: (_: boolean) => { },
  onSubscriptionNotesChange: (_: string) => { },
  onSubscriptionCreditsChange: (_: ECreditType, _2: number) => { },
  onSubscriptionSenderChange: (_: string) => { },
  onSubscriptionPlanStartChange: (_: Date) => { },
  onSubscriptionPlanEndChange: (_: Date) => { },
  onEmployeeInChargeChange: (_: string) => { },
  onSubscriptionDataSourceChange: (_: SubscriptionDataSource) => { },
  onAIActionChange: (_: string) => { },
  onAIBehaviourChange: (_: string) => { },
  onAIWebsiteChange: (_: string) => { },
  onAIConstraintsChange: (_: string) => { },
  onAIDomainChange: (_: string) => { }, // NOTE : This has become a domainId
  onAIFullnameChange: (_: string) => { },
  onAIGenderChange: (_: AIGender) => { },
  onAIIntroChange: (_: string) => { },
  onAIFirstReminderTimingChange: (_: number) => { },
  onAISecondReminderTimingChange: (_: number) => { },
  onAILocationChange: (_: string) => { },
  onAIProfileChange: (_: string) => { },
  onAITargetChange: (_: string) => { },
  onAITonChange: (_: AITon) => { },
  submit: () => Promise.resolve(),
});

export const UserCreationFormProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const authContext = useContext(AuthenticationContext);

  const [status, setStatus] = useState(ContextStatus.IDLE);
  const [user, setUser] = useState<UserInfosPayload>(
    UserInfosPayload.newDefault()
  );
  const [linkedSubscriptionId, setLinkedSubscriptionId] = useState('');
  const [linkedCompanyId, setLinkedCompanyId] = useState('');
  const [subscription, setSubscription] = useState<SubscriptionPayload>(
    SubscriptionPayload.newDefault(authContext.authentication?.id)
  );
  const [ai, setAI] = useState<AIPayload>(AIPayload.newDefault());
  const [errorMessage, setErrorMessage] = useState('');

  const [selectedCountry, setSelectedCountry] = useState({ name: 'france', code: '33', digitsLength: 9 });
  const [phoneNumber, setPhoneNumber] = useState('');

  // User infos methods
  const onAvatarChange = (avatar: string) => {
    setUser(user.copyWith({ avatar }));
  };

  const onUserEmailChange = (email: string) => {
    setUser(user.copyWith({ email }));
  };

  const onUserEmailAuthenticationChange = (emailAuthentication: string) => {
    setUser(user.copyWith({ emailAuthentication }));
  };

  const onUserPasswordChange = (password: string) => {
    setUser(user.copyWith({ password }));
  };

  const onUserRoleChange = (role: UserRole) => {
    setUser(user.copyWith({ role }));
  };

  const onUserNotesChange = (notes: string) => {
    setUser(user.copyWith({ notes }));
  };

  const onUserFirstnameChange = (firstname: string) => {
    setUser(user.copyWith({ firstname }));
  };

  const onUserLastnameChange = (lastname: string) => {
    setUser(user.copyWith({ lastname }));
  };

  const onUserPhoneChange = (phone: string) => {
    setUser(user.copyWith({ phone }));
  };

  const onUserSelectedCountryCodeChange = (selectedCountry: { name: string; code: string; digitsLength: number }) => {
    setSelectedCountry(selectedCountry);

    if (phoneNumber) {
      const cleanedPhoneNumber = removeLeadingZeros(phoneNumber);
      const fullPhone = formatFullPhoneNumber(cleanedPhoneNumber, selectedCountry.code);
      onUserPhoneChange(fullPhone);
    }
  };

  const onUserPhoneNumberChange = (newPhoneNumber: string) => {
    const digitsOnly = cleanPhoneNumber(newPhoneNumber);
    const cleanedPhoneNumber = removeLeadingZeros(digitsOnly);

    setPhoneNumber(cleanedPhoneNumber);

    const fullPhone = formatFullPhoneNumber(cleanedPhoneNumber, selectedCountry.code);
    onUserPhoneChange(fullPhone);
  };

  const onUserTypeChange = (type: UserType) => {
    setUser(user.copyWith({ type }));
  };

  // Linked subscription methods
  const onLinkedSubscriptionIdChange = (id: string) => {
    setLinkedSubscriptionId(id);
  };

  const onLinkedCompanyIdChange = (id: string) => {
    setLinkedCompanyId(id);
  };

  // Subscription methods
  const onSubscriptionIdPlanChange = (idPlan: string) => {
    setSubscription(subscription.copyWith({ idPlan }));
  };

  const onSubscriptionTimezoneCityChange = (city: SubscriptionTimezoneCity) => {
    setSubscription(
      subscription.copyWith({
        timezone: subscription.timezone.copyWith({ city }),
      })
    );
  };

  const onSubscriptionTimezoneContinentChange = (
    continent: SubscriptionTimezoneContinent
  ) => {
    setSubscription(
      subscription.copyWith({
        timezone: subscription.timezone.copyWith({ continent }),
      })
    );
  };

  const onSubscriptionTimezoneCountryChange = (
    country: SubscriptionTimezoneCountry
  ) => {
    setSubscription(
      subscription.copyWith({
        timezone: subscription.timezone.copyWith({ country }),
      })
    );
  };

  const onSubscriptionNameChange = (name: string) => {
    setSubscription(subscription.copyWith({ name }));
  };

  const onSubscriptionSettingsEmailNotificationCopyChange = (copies: string[]) => {
    setSubscription(
      subscription.copyWith({
        settings: {
          ...subscription.settings,
          email_notification_copy: copies,
        } as SubscriptionSettings,
      })
    );
  };

  const onSubscriptionSettingsBetaAccountChange = (beta_account: boolean) => {
    setSubscription(
      subscription.copyWith({
        settings: {
          ...subscription.settings,
          beta_account,
        } as SubscriptionSettings,
      })
    );
  };

  const onSubscriptionSettingsDemoAccountChange = (demo_account: boolean) => {
    setSubscription(
      subscription.copyWith({
        settings: {
          ...subscription.settings,
          demo_account,
        } as SubscriptionSettings,
      })
    );
  };

  const onSubscriptionSettingsPaygEnabledChange = (payg_enabled: boolean) => {
    setSubscription(
      subscription.copyWith({
        settings: {
          ...subscription.settings,
          payg_enabled,
        } as SubscriptionSettings,
      })
    );
  };

  const onSubscriptionSettingsPaymentWallChange = (payment_wall: boolean) => {
    setSubscription(
      subscription.copyWith({
        settings: {
          ...subscription.settings,
          payment_wall,
        } as SubscriptionSettings,
      })
    );
  };

  const onSubscriptionNotesChange = (notes: string) => {
    setSubscription(subscription.copyWith({ notes }));
  };

  const onSubscriptionSenderChange = (sender: string) => {
    setSubscription(subscription.copyWith({ sender }));
  };

  const onSubscriptionCreditsChange = (type: ECreditType, value: number) => {
    setSubscription(subscription.copyWith({ credits: { ...subscription.credits, [type]: value } }));
  };

  const onSubscriptionPlanStartChange = (planStart: Date) => {
    setSubscription(subscription.copyWith({ planStart }));
  };

  const onSubscriptionPlanEndChange = (planEnd: Date) => {
    setSubscription(subscription.copyWith({ planEnd }));
  };

  const onSubscriptionDataSourceChange = (datasource: SubscriptionDataSource) => {
    setSubscription(subscription.copyWith({ datasource }))
  };

  // AI methods
  const onAIActionChange = (action: string) => {
    setAI(ai.copyWith({ action }));
  };

  const onAIBehaviourChange = (behaviour: string) => {
    setAI(ai.copyWith({ behaviour }));
  };

  const onAIWebsiteChange = (website: string) => {
    setAI(ai.copyWith({ website }));
  };

  const onAIConstraintsChange = (constraints: string) => {
    setAI(ai.copyWith({ constraints }));
  };

  const onAIDomainChange = (domain: string) => {
    setAI(ai.copyWith({ domain }));
  };

  const onAIFullnameChange = (fullname: string) => {
    setAI(ai.copyWith({ fullname }));
  };

  const onAIGenderChange = (gender: AIGender) => {
    setAI(ai.copyWith({ gender }));
  };

  const onAIIntroChange = (intro: string) => {
    setAI(ai.copyWith({ intro }));
  };

  const onAIFirstReminderTimingChange = (firstReminder: number) => {
    setAI(
      ai.copyWith({
        reminderTiming: [firstReminder, ai.reminderTiming[1]],
      })
    );
  };

  const onAISecondReminderTimingChange = (secondReminder: number) => {
    setAI(
      ai.copyWith({
        reminderTiming: [ai.reminderTiming[0], secondReminder],
      })
    );
  };

  const onAILocationChange = (location: string) => {
    setAI(ai.copyWith({ location }));
  };

  const onAIProfileChange = (profile: string) => {
    setAI(ai.copyWith({ profile }));
  };

  const onAITargetChange = (target: string) => {
    setAI(ai.copyWith({ target }));
  };

  const onAITonChange = (ton: AITon) => {
    setAI(ai.copyWith({ ton }));
  }

  const onEmployeeInChargeChange = (employeeInChargeUid: string) => {
    setSubscription(subscription.copyWith({ employeeInChargeUid }))
  }

  const submit = async () => {
    if (authContext.authentication === null) {
      return;
    }

    setStatus(ContextStatus.LOADING);
    try {
      await createUser(
        new CreateUserPayload(
          user,
          linkedSubscriptionId,
          linkedCompanyId,
          subscription,
          ai
        ),
        authContext.authentication
      );

      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      if (e instanceof CreateUserFailure) {
        setErrorMessage(formatErrorMessage(e.message));
      }
      setStatus(ContextStatus.ERROR);
    }
  };

  return (
    <UserCreationFormContext.Provider
      value={{
        status,
        user,
        linkedSubscriptionId,
        linkedCompanyId,
        subscription,
        ai,
        errorMessage,
        selectedCountry,
        phoneNumber,
        onAvatarChange,
        onUserEmailChange,
        onUserEmailAuthenticationChange,
        onUserPasswordChange,
        onUserRoleChange,
        onUserNotesChange,
        onUserFirstnameChange,
        onUserLastnameChange,
        onUserPhoneChange,
        onUserSelectedCountryCodeChange,
        onUserPhoneNumberChange,
        onUserTypeChange,
        onLinkedSubscriptionIdChange,
        onLinkedCompanyIdChange,
        onSubscriptionIdPlanChange,
        onSubscriptionTimezoneCityChange,
        onSubscriptionTimezoneContinentChange,
        onSubscriptionTimezoneCountryChange,
        onSubscriptionNameChange,
        onSubscriptionSettingsEmailNotificationCopyChange,
        onSubscriptionSettingsBetaAccountChange,
        onSubscriptionSettingsDemoAccountChange,
        onSubscriptionSettingsPaygEnabledChange,
        onSubscriptionSettingsPaymentWallChange,
        onSubscriptionNotesChange,
        onSubscriptionCreditsChange,
        onSubscriptionSenderChange,
        onSubscriptionPlanStartChange,
        onSubscriptionPlanEndChange,
        onEmployeeInChargeChange,
        onSubscriptionDataSourceChange,
        onAIActionChange,
        onAIBehaviourChange,
        onAIWebsiteChange,
        onAIConstraintsChange,
        onAIDomainChange,
        onAIFullnameChange,
        onAIGenderChange,
        onAIIntroChange,
        onAIFirstReminderTimingChange,
        onAISecondReminderTimingChange,
        onAILocationChange,
        onAIProfileChange,
        onAITargetChange,
        onAITonChange,
        submit,
      }}
    >
      {children}
    </UserCreationFormContext.Provider>
  );
};

export default UserCreationFormContext;
